import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  MagnifyingGlass,
  EnvelopeSimple,
  PaperPlaneTilt,
  CheckCircle,
  WarningCircle,
  ArrowUUpLeft,
} from "@phosphor-icons/react";
import { AppHeader } from "@/components/AppHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const FILTERS = [
  ["", "All"],
  ["sent", "Delivered"],
  ["failed", "Failed"],
  ["fallback", "Used backup"],
];

const when = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "—";

const Stat = ({ label, value, testid, tone = "" }) => (
  <div
    className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"
    data-testid={testid}
  >
    <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
      {label}
    </div>
    <div className={`font-mono-data mt-1 text-xl font-bold ${tone}`}>
      {value}
    </div>
  </div>
);

export default function EmailActivity() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [fixing, setFixing] = useState(null);
  const [fixTo, setFixTo] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const params = {};
    if (status) params.status = status;
    if (q.trim()) params.q = q.trim();
    const { data } = await api.get("/email-log", { params });
    setRows(data.emails || []);
    setTotals(data.totals || {});
  }, [status, q]);

  const resend = async (row) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/email-log/${row.id}/resend`, {
        to: fixTo.trim() || row.to,
      });
      toast.success(`Sent to ${data.to}`);
      setFixing(null);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "It would not send again",
      );
    }
    await load();
    setBusy(false);
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Email activity" />
      <main className="relative z-10 pb-28 pt-6">
        <button
          type="button"
          data-testid="email-activity-back"
          onClick={() => nav("/settings")}
          className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 hover:text-zinc-200"
        >
          <ArrowLeft size={13} weight="bold" /> Settings
        </button>
        <div className="mt-3 flex items-center gap-3">
          <EnvelopeSimple size={22} weight="bold" className="text-indigo-300" />
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
            Email activity
          </h1>
        </div>
        <p className="mt-1 max-w-2xl text-sm text-zinc-500">
          Every quote, invoice, portal invite and reminder the app has sent —
          who it went to and whether Microsoft 365 or the built-in sender
          delivered it.
        </p>

        <div className="mt-5 grid gap-3 sm:grid-cols-4">
          <Stat
            label="Delivered"
            value={totals.sent ?? 0}
            testid="email-stat-sent"
          />
          <Stat
            label="Microsoft 365"
            value={totals.microsoft ?? 0}
            testid="email-stat-microsoft"
          />
          <Stat
            label="Built-in sender"
            value={totals.managed ?? 0}
            testid="email-stat-managed"
          />
          <Stat
            label="Failed"
            value={totals.failed ?? 0}
            testid="email-stat-failed"
            tone={totals.failed ? "text-red-400" : ""}
          />
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          {FILTERS.map(([value, label]) => (
            <button
              key={label}
              type="button"
              data-testid={`email-filter-${label.toLowerCase().replace(/ /g, "-")}`}
              onClick={() => setStatus(value)}
              className={`h-12 flex-1 rounded-md border px-4 text-[11px] font-bold uppercase tracking-widest transition-colors duration-150 sm:flex-none ${
                status === value
                  ? "border-indigo-500 bg-indigo-500/15 text-indigo-200"
                  : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:border-zinc-700"
              }`}
            >
              {label}
            </button>
          ))}
          <div className="relative ml-auto w-full sm:w-72">
            <MagnifyingGlass
              size={15}
              weight="bold"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
            />
            <Input
              data-testid="email-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search address or subject"
              className="h-12 border-zinc-800 bg-zinc-900 pl-9"
            />
          </div>
        </div>

        <div
          className="mt-4 divide-y divide-zinc-800/70 rounded-lg border border-zinc-800 bg-zinc-900"
          data-testid="email-activity-list"
        >
          {rows.length === 0 && (
            <p className="p-6 text-sm text-zinc-500">
              Nothing here yet — emails appear the moment the app sends one.
            </p>
          )}
          {rows.map((r) => (
            <div
              key={r.id}
              data-testid={`email-row-${r.id}`}
              className="flex flex-wrap items-start gap-3 p-4"
            >
              {r.ok ? (
                <CheckCircle
                  size={18}
                  weight="bold"
                  className="mt-0.5 shrink-0 text-emerald-400"
                />
              ) : (
                <WarningCircle
                  size={18}
                  weight="bold"
                  className="mt-0.5 shrink-0 text-red-400"
                />
              )}
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold">
                  {r.subject}
                </div>
                <div className="mt-0.5 text-xs text-zinc-500">
                  To {r.to} · {when(r.at)}
                </div>
                {r.error && (
                  <div className="mt-1 flex items-start gap-1.5 text-xs text-amber-400">
                    {r.fell_back && (
                      <ArrowUUpLeft
                        size={13}
                        weight="bold"
                        className="mt-0.5 shrink-0"
                      />
                    )}
                    <span className="break-words">
                      {r.fell_back ? "Microsoft refused it: " : ""}
                      {r.error}
                    </span>
                  </div>
                )}
                {r.resent_at && (
                  <div
                    className="mt-1 text-xs text-emerald-400"
                    data-testid={`email-resent-${r.id}`}
                  >
                    Sent again to {r.resent_to} on {when(r.resent_at)}
                  </div>
                )}
                {r.can_resend && !r.resent_at && fixing !== r.id && (
                  <button
                    type="button"
                    data-testid={`email-resend-${r.id}`}
                    onClick={() => {
                      setFixing(r.id);
                      setFixTo(r.to);
                    }}
                    className="mt-2 flex items-center gap-1.5 rounded-full border border-zinc-700 px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:border-indigo-500 hover:text-indigo-200"
                  >
                    <PaperPlaneTilt size={12} weight="bold" /> Fix address and
                    send again
                  </button>
                )}
                {fixing === r.id && (
                  <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                    <Input
                      data-testid={`email-resend-to-${r.id}`}
                      value={fixTo}
                      autoFocus
                      onChange={(e) => setFixTo(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") resend(r);
                      }}
                      placeholder="correct@address.com"
                      className="h-10 border-zinc-800 bg-zinc-950 sm:max-w-xs"
                    />
                    <div className="flex gap-2">
                      <Button
                        data-testid={`email-resend-send-${r.id}`}
                        disabled={busy}
                        onClick={() => resend(r)}
                        className="h-10 bg-emerald-500 text-[10px] font-bold uppercase tracking-widest text-white hover:bg-emerald-400"
                      >
                        Send again
                      </Button>
                      <Button
                        data-testid={`email-resend-cancel-${r.id}`}
                        variant="outline"
                        onClick={() => setFixing(null)}
                        className="h-10 border-zinc-800 bg-zinc-950 text-[10px] font-bold uppercase tracking-widest"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              <span
                className={`shrink-0 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest ${
                  r.provider === "microsoft_graph"
                    ? "border-blue-500/25 bg-blue-500/10 text-blue-300"
                    : "border-zinc-600/25 bg-zinc-700/10 text-zinc-400"
                }`}
              >
                {r.provider_label}
              </span>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
